import React, { useEffect, useState } from 'react';
import { Heading } from '../../common'; // adjust imports as needed
import DesignDropdown from './DesignDropdown';
import { InputText } from '../../components/form';
import { values } from 'lodash';

// comparibility_ID is an MSTCompatibility id and must stay numeric in the AND/OR payloads.
const toComparibilityId = value => {
  if (value === null || value === undefined || value === '') return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const ConditionalDisplayLogic = ({
  checkboxDisplay,
  taskQuestion,
  formTaskSections,
  sectionIndex,
  dropDownQuestionOptions,
  dropdownSectionIndex,
  setDropdownQuestions,
  setDropdownSectionIndex,
  setTaskQuestion,
  attachedChoiceAfter,
  likertSelectedChoice,
  answerChoice,
  inputAnswer,
  comparibilityValue,
  setORLogic,
  setAndLogic,
  logicValue,
  item,
  hideId,
  selectedSectionValue,
  onSectionChange,
  parentSequence,
  questionIndex,
  setLikertSelectedChoice,
  onQuestionChange
}) => {
  if (checkboxDisplay !== 1) return null;
  const sectionOptions = formTaskSections
    .filter((_, index) => index <= sectionIndex)
    .map((_, index) => ({
      value: index + 1,
      label: index + 1
    }));
  const [selectedQuestion, setSelectedQuestion] = useState();
  const [selectedSection, setSelectedSection] = useState(selectedSectionValue !== undefined ? Number(selectedSectionValue) - 1 : undefined);
  const [hasEdited, setHasEdited] = useState(false);
  const [logicInitialized, setLogicInitialized] = useState(false);

  const clearLogicForThisRow = prev => {
    const items = Array.isArray(prev) ? prev : [];
    return items.filter(logic => Number(logic?.parentSequence) !== Number(parentSequence));
  };

  const handleQuestionChange = e => {
    const activeSectionIndex =
      selectedSection !== undefined
        ? selectedSection
        : selectedSectionValue !== undefined
          ? Number(selectedSectionValue) - 1
          : 0;
    const value = e?.value;
    const questionId = e?.questionId || e?.question_ID;

    let selectedQuestionAttached;
    if (questionId !== undefined && questionId !== null && questionId !== '') {
      selectedQuestionAttached = formTaskSections[activeSectionIndex]?.questionName_array?.find(
        q => String(q.question_ID) === String(questionId)
      );
    }
    if (!selectedQuestionAttached && value !== undefined) {
      const optionIndex = Number(value);
      selectedQuestionAttached = formTaskSections[activeSectionIndex]?.questionName_array?.[optionIndex];
    }

    setSelectedSection(activeSectionIndex);
    setSelectedQuestion({
      selectedQuestionAttached,
      selectedChoiceID: null,
      selectedChoiceValue: null,
      selectedComparibilityID: null,
      LikertScaleID: null,
      npsScale_ID: null
    });
    setHasEdited(true);
    // Ranking → value (or any parent switch): drop previous question for this row.
    setAndLogic(clearLogicForThisRow);
    setORLogic(clearLogicForThisRow);
    if (typeof onQuestionChange === 'function') {
      onQuestionChange(selectedQuestionAttached);
    }
  };

  useEffect(() => {
    if (hasEdited || logicInitialized) return;
    
    // Debug logging for first item
    if (item?.parentSequence === 2) {
      // console.log('Processing first item (sequence 2):', item);
    }
    
    // Set the selected question data
    if (item && item.selectedQuestionAttached) {
      setSelectedQuestion(prev => ({ ...prev, selectedQuestionAttached: item.selectedQuestionAttached }));
    }
    if (item?.selectedQuestionSectionAttached || selectedSectionValue !== undefined) {
      const sectionIndex = item?.selectedQuestionSectionAttached
        ? Number(item.selectedQuestionSectionAttached) - 1
        : Number(selectedSectionValue) - 1;
      setSelectedSection(sectionIndex);
    }
    const itemParentId = item?.parentQuestion_ID ?? item?.parentQuestionAttached;
    const itemParentType = item?.parentQuestionType ?? item?.parentQuestionType_ID;
    // If we're not editing (item has existing data), set the logic data correctly
    if ((item && itemParentType != null && itemParentId != null) || item?.selectedQuestionSectionAttached) {
      let questionType = itemParentType;
      if (questionType) {
        let tempLogic = {
          parentQuestionType_ID: itemParentType,
          parentQuestionAttached: itemParentId,
          questionIndex: questionIndex, // Add index to track specific instance
          parentSequence: item?.parentSequence,
        };

        // Handle different question types
        if ([1, 2, 7].includes(questionType)) {
          if (item?.choice_ID !== undefined && item?.choice_ID !== null) {
            const attachedQuestion = {};
            attachedQuestion[String(itemParentId)] = [String(item.choice_ID)];
            tempLogic = { ...tempLogic, questionAttached: attachedQuestion };
          }
        } else if ([5, 6].includes(questionType) && item.ifContains) {
          tempLogic = { ...tempLogic, ifContains: item.ifContains };
        } else if ([3, 11].includes(questionType) && item.comparibility_value && item.comparibility_ID) {
          tempLogic = {
            ...tempLogic,
            comparibility_value: item.comparibility_value,
            comparibility_ID: toComparibilityId(item.comparibility_ID)
          };
        } else if (questionType === 14 && item.comparibility_value && item.comparibility_ID) {
          const attachedQuestion = {};
          attachedQuestion[String(item.choice_ID)] = [String(item.rank_IDAttached || item.choice_ID)];
          tempLogic = {
            ...tempLogic,
            questionAttached: attachedQuestion,
            comparibility_value: item.comparibility_value,
            comparibility_ID: toComparibilityId(item.comparibility_ID)
          };
        } else if ((questionType === 4 || questionType === 16) && item.questionAttached) {
          tempLogic = { ...tempLogic, questionAttached: item.questionAttached };
        }
        // Set the logic based on the condition
        if (tempLogic?.parentQuestionAttached != hideId) {
          if (logicValue === 1) {
            setORLogic(prevItems => {
              const items = Array.isArray(prevItems) ? prevItems : [];
              // Check if this is the first item (parentSequence === 2)
              if (tempLogic.parentSequence === 2) {
                const existingFirstItemIndex = items.findIndex(logic => logic.parentSequence === 2);
                if (existingFirstItemIndex !== -1) {
                  // Update existing first item
                  const updatedItems = [...items];
                  updatedItems[existingFirstItemIndex] = {
                    ...updatedItems[existingFirstItemIndex],
                    ...tempLogic,
                    parentSequence: 2
                  };
                  return updatedItems;
                }
              }
              // For other cases, check if item already exists before adding
              const existingItemIndex = items.findIndex(
                logic => Number(logic.parentSequence) === Number(tempLogic.parentSequence)
              );
              if (existingItemIndex !== -1) {
                // Update existing item
                const updatedItems = [...items];
                updatedItems[existingItemIndex] = {
                  ...updatedItems[existingItemIndex],
                  ...tempLogic
                };
                return updatedItems;
              }
              // Add new item only if it doesn't exist
              return [...items, tempLogic];
            });
            // Remove from AND logic if it exists
            setAndLogic(prevItems => {
              const items = Array.isArray(prevItems) ? prevItems : [];
              return items.filter(item => 
                !(item.parentQuestionType_ID === tempLogic.parentQuestionType_ID && 
                  item.parentSequence === parentSequence && item.questionIndex === questionIndex)
              );
            });
          } else {
            setAndLogic(prevItems => {
              const items = Array.isArray(prevItems) ? prevItems : [];
              // Check if this is the first item (parentSequence === 2)
              if (tempLogic.parentSequence === 2) {
                const existingFirstItemIndex = items.findIndex(logic => logic.parentSequence === 2);
                if (existingFirstItemIndex !== -1) {
                  // Update existing first item
                  const updatedItems = [...items];
                  updatedItems[existingFirstItemIndex] = {
                    ...updatedItems[existingFirstItemIndex],
                    ...tempLogic,
                    parentSequence: 2
                  };
                  return updatedItems;
                }
              }
              // For other cases, check if item already exists before adding
              const existingItemIndex = items.findIndex(
                logic => Number(logic.parentSequence) === Number(tempLogic.parentSequence)
              );
              if (existingItemIndex !== -1) {
                // Update existing item
                const updatedItems = [...items];
                updatedItems[existingItemIndex] = {
                  ...updatedItems[existingItemIndex],
                  ...tempLogic
                };
                return updatedItems;
              }
              // Add new item only if it doesn't exist
              return [...items, tempLogic];
            });
            // Remove from OR logic if it exists
            setORLogic(prevItems => {
              const items = Array.isArray(prevItems) ? prevItems : [];
              return items.filter(item => 
                !(item.parentQuestionType_ID === tempLogic.parentQuestionType_ID && 
                  item.parentSequence === parentSequence && item.questionIndex === questionIndex)
              );
            });
          }
        }
      }
    }
    
    // Mark logic as initialized to prevent re-running
    setLogicInitialized(true);
    
    if ((item?.parentQuestionType ?? item?.parentQuestionType_ID) == 14) {
      const qaEntry = item?.questionAttached && Object.entries(item.questionAttached)[0];
      const rankFromAttached = Array.isArray(qaEntry?.[1]) ? qaEntry[1][0] : qaEntry?.[1];
      const savedRankId = item?.rank_IDAttached ?? item?.choice_ID ?? rankFromAttached;
      setSelectedQuestion(prev => ({
        ...prev,
        selectedQuestionAttached: item.selectedQuestionAttached,
        selectedChoiceValue: item?.comparibility_value || '',
        selectedComparibilityID: item?.comparibility_ID || '',
        selectedChoiceID: savedRankId != null && savedRankId !== '' ? savedRankId : null
      }));
    } else {
      // Type 4 often stores scale as questionAttached: { [choiceId]: [likertId] }
      const qaEntry = item?.questionAttached && Object.entries(item.questionAttached)[0];
      const likertId =
        item?.likert_IDAttached ?? (Array.isArray(qaEntry?.[1]) ? qaEntry[1][0] : qaEntry?.[1]);
      const choiceId = item?.choice_ID ?? qaEntry?.[0];

      setSelectedQuestion(prev => ({
        ...prev,
        selectedQuestionAttached: item.selectedQuestionAttached,
        selectedChoiceValue: item?.comparibility_value || '',
        selectedComparibilityID: item?.comparibility_ID || '',
        selectedChoiceID: choiceId != null && choiceId !== '' ? choiceId : null,
        LikertScaleID: likertId ?? prev?.LikertScaleID,
        npsScale_ID: likertId ?? prev?.npsScale_ID
      }));
    }
  }, [item, logicValue]);

  // Reset logic initialization when item changes
  useEffect(() => {
    if (selectedSectionValue !== undefined && selectedSectionValue !== null && selectedSectionValue !== '') {
      setSelectedSection(Number(selectedSectionValue) - 1);
    } else if (!hasEdited) {
      // New multi-display row: start with no section (don't inherit previous row).
      setSelectedSection(undefined);
    }
  }, [selectedSectionValue, hasEdited]);

  useEffect(() => {
    setLogicInitialized(false);
  }, [item]);
  const updateLogicArray = (prevItems, newItem) => {
    const items = Array.isArray(prevItems) ? prevItems : [];
    // Replace by row sequence so switching ranking → value does not keep both parents.
    const existingLogicIndex = items.findIndex(
      logic => Number(logic?.parentSequence) === Number(newItem.parentSequence)
    );

    if (existingLogicIndex !== -1) {
      const updatedItems = [...items];
      updatedItems[existingLogicIndex] = {
        ...updatedItems[existingLogicIndex],
        ...newItem,
        parentSequence: newItem.parentSequence
      };
      return updatedItems;
    }
    return [...items, newItem];
  };

  const removeFromOtherLogicArray = () => {
    if (logicValue === 1) {
      setAndLogic(clearLogicForThisRow);
    } else {
      setORLogic(clearLogicForThisRow);
    }
  };

  useEffect(() => {
    if (selectedQuestion?.selectedQuestionAttached) {
      // Debug logging for first item updates
      let tempLogic = {
        parentQuestionType_ID: selectedQuestion?.selectedQuestionAttached?.questionType_ID,
        parentQuestionAttached: selectedQuestion?.selectedQuestionAttached?.question_ID,
        parentSequence: parentSequence,
        questionIndex: questionIndex,
      };

      // Handle different question types based on the payload structure
      const questionType = selectedQuestion?.selectedQuestionAttached?.questionType_ID;

      // Type 1, 2, 4, 7 - Multiple choice, single choice, likert scale, ranking
      if ([1, 2, 7].includes(questionType)) {
        if (selectedQuestion?.selectedChoiceID) {
          const selectedChoices = [];
          const attachedQuestion = {};
          selectedChoices.push(String(selectedQuestion.selectedChoiceID));
          attachedQuestion[String(selectedQuestion.selectedQuestionAttached.question_ID)] = selectedChoices;
          tempLogic = { ...tempLogic, questionAttached: attachedQuestion };
          if (logicValue === 1) {
            setORLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          } else {
            setAndLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          }
        }
      }

      // Type 5, 6 - Text answer questions with ifContains
      else if ([5, 6].includes(questionType)) {
        if (selectedQuestion?.selectedChoiceID) {
          tempLogic = { ...tempLogic, ifContains: selectedQuestion.selectedChoiceID };
          if (logicValue === 1) {
            setORLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          } else {
            setAndLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          }
        }
      }

      // Type 3, 11 - Value questions with comparibility
      else if ([3, 11].includes(questionType)) {
        const comparibilityId = hasEdited
          ? selectedQuestion.selectedComparibilityID
          : selectedQuestion.selectedComparibilityID || item?.comparibility_ID;
        const comparibilityValue = hasEdited
          ? selectedQuestion.selectedChoiceValue
          : selectedQuestion.selectedChoiceValue || item?.comparibility_value;
        // After switching from ranking, wait until user picks operator (don't keep old ranking op).
        if (comparibilityId != null && comparibilityId !== '') {
          tempLogic = {
            ...tempLogic,
            comparibility_value: String(comparibilityValue ?? ''),
            comparibility_ID: toComparibilityId(comparibilityId)
          };
          if (logicValue === 1) {
            setORLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          } else {
            setAndLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          }
        }
      }

      // Type 14 - Ranking questions with both questionAttached and comparibility
      else if (questionType === 14 && hasEdited) {
        if (selectedQuestion?.selectedChoiceID && selectedQuestion?.selectedComparibilityID) {
          const selectedChoices = [];
          const attachedQuestion = {};
          const parentQuestionId = selectedQuestion?.selectedQuestionAttached?.question_ID;
          selectedChoices.push(String(selectedQuestion?.selectedChoiceID || item?.rank_IDAttached));

          attachedQuestion[String(parentQuestionId)] = selectedChoices;

          const tempLogic = {
            parentQuestionType_ID: selectedQuestion?.selectedQuestionAttached?.questionType_ID,
            parentQuestionAttached: parentQuestionId,
            questionAttached: attachedQuestion,
            comparibility_value: String(selectedQuestion?.selectedChoiceValue || item?.comparibility_value),
            comparibility_ID: toComparibilityId(selectedQuestion.selectedComparibilityID || item?.comparibility_ID),
            questionIndex: questionIndex,
            parentSequence: parentSequence,
          };

          if (logicValue === 1) {
            setORLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          } else {
            setAndLogic(prev => updateLogicArray(prev, tempLogic));
            removeFromOtherLogicArray();
          }
        }
      } else if (questionType === 4 || questionType === 16) {
        const selectedChoices = [];
        const likertChoices = [];
        const attachedQuestion = {};
        likertChoices.push(String(selectedQuestion.LikertScaleID || item?.likert_IDAttached));
        selectedChoices.push(String(selectedQuestion.selectedChoiceID || item.choice_ID));
        if (questionType === 4) {
          attachedQuestion[String(selectedChoices)] = likertChoices;
        }
        if (questionType === 16) {
          const npsValue =
            selectedQuestion?.npsScale_ID != null && selectedQuestion?.npsScale_ID !== ''
              ? selectedQuestion.npsScale_ID
              : item?.likert_IDAttached;
          attachedQuestion[
            String(
              selectedQuestion?.selectedQuestionAttached?.choiceName_array[0]?.id ||
                selectedQuestion?.selectedQuestionAttached?.choiceName_array[0]?.choiceName_ID
            )
          ] = String(npsValue);
        }
        tempLogic = { ...tempLogic, questionAttached: attachedQuestion, questionIndex: questionIndex,
            parentSequence: parentSequence, };
        if (logicValue === 1) {
          setORLogic(prev => updateLogicArray(prev, tempLogic));
          removeFromOtherLogicArray();
        } else {
          setAndLogic(prev => updateLogicArray(prev, tempLogic));
          removeFromOtherLogicArray();
        }
      }
    }
  }, [selectedQuestion, logicValue, setORLogic, setAndLogic, parentSequence, questionIndex, hasEdited]);

  const qaEntry = item?.questionAttached && Object.entries(item.questionAttached)[0];
  const savedLikertOrNpsId =
    item?.likert_IDAttached ?? (Array.isArray(qaEntry?.[1]) ? qaEntry[1][0] : qaEntry?.[1]);

  // Keep Likert and NPS IDs separate so NPS selection doesn't depend on shared likertSelectedChoice.
  const attachedLikertId = hasEdited
    ? selectedQuestion?.LikertScaleID
    : selectedQuestion?.LikertScaleID ?? savedLikertOrNpsId;
  const attachedNpsId = hasEdited
    ? selectedQuestion?.npsScale_ID
    : selectedQuestion?.npsScale_ID ?? savedLikertOrNpsId;

  const attachedQ = hasEdited
    ? selectedQuestion?.selectedQuestionAttached
    : selectedQuestion?.selectedQuestionAttached || item?.selectedQuestionAttached;

  const resolveNpsDropdownValue = () => {
    if (attachedNpsId == null || attachedNpsId === '') return [];
    const scales = selectedQuestion?.selectedQuestionAttached?.likertScaleName_array || [];
    const asIndex = Number(attachedNpsId);
    if (Number.isInteger(asIndex) && asIndex >= 0 && asIndex <= 10 && scales[asIndex]) {
      // Prefer index when IDs are null (new NPS scales) or value itself is 0-10.
      const byId = scales.findIndex(s => String(s.questionLikertScale_ID) === String(attachedNpsId));
      if (byId < 0 || scales[asIndex]?.questionLikertScale_ID == null) {
        return [{ label: `${asIndex}`, value: scales[asIndex]?.questionLikertScale_ID ?? asIndex }];
      }
    }
    const byIdIndex = scales.findIndex(s => String(s.questionLikertScale_ID) === String(attachedNpsId));
    if (byIdIndex < 0) return []; // never show findIndex "-1"
    return [
      {
        label: `${byIdIndex}`,
        value: scales[byIdIndex].questionLikertScale_ID ?? byIdIndex
      }
    ];
  };

  // Screener ranking often uses QuestionChoice_ID; only show a value once selected/saved.
  const getChoiceOptionId = choice =>
    choice?.choiceName_ID ?? choice?.id ?? choice?.QuestionChoice_ID ?? choice?.questionChoices_ID;

  const resolveChoiceDropdownValue = choiceId => {
    if (choiceId == null || choiceId === '') return [];
    const choices = selectedQuestion?.selectedQuestionAttached?.choiceName_array || [];
    const match = choices.find(c => String(getChoiceOptionId(c)) === String(choiceId));
    return match ? [{ label: match.choiceName, value: getChoiceOptionId(match) }] : [];
  };

  const resolveSectionDropdownValue = () => {
    // Prefer this row's live/local section — never inherit another row via
    // taskQuestion.selectedQuestionSectionAttached (that caused stale section on "add new").
    if (hasEdited && selectedSection !== undefined && selectedSection !== null) {
      return selectedSection + 1;
    }
    if (selectedSectionValue !== undefined && selectedSectionValue !== null && selectedSectionValue !== '') {
      return selectedSectionValue;
    }
    if (selectedSection !== undefined && selectedSection !== null) {
      return selectedSection + 1;
    }
    return null;
  };
  const sectionDropdownValue = resolveSectionDropdownValue();

  return (
    <>
      <div className="d-flex mt-2 mb-3" style={{ columnGap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <DesignDropdown
          key={`cdl-section-${parentSequence}-${sectionDropdownValue ?? 'none'}`}
          options={sectionOptions}
          value={
            sectionDropdownValue != null
              ? [
                  {
                    label: sectionDropdownValue,
                    value: sectionDropdownValue
                  }
                ]
              : []
          }
          controlStyleWidth="80px"
          onChange={e => {
            setDropdownQuestions(Number(e.value) - 1);
            setDropdownSectionIndex(Number(e.value) - 1);
            setSelectedSection(Number(e.value) - 1);
            setSelectedQuestion({
              selectedQuestionAttached: null,
              selectedChoiceID: null,
              selectedChoiceValue: null,
              selectedComparibilityID: null,
              LikertScaleID: null,
              npsScale_ID: null
            });
            setHasEdited(true);
            if (onSectionChange) onSectionChange(Number(e.value));
          }}
        />

        <DesignDropdown
          key={attachedQ?.question_ID}
          options={dropDownQuestionOptions}
          value={
            attachedQ?.question_ID
              ? [
                  dropDownQuestionOptions?.find(o => String(o.questionId) === String(attachedQ.question_ID)) || {
                    label: attachedQ.questionName,
                    value: attachedQ.question_ID,
                    questionId: attachedQ.question_ID
                  }
                ]
              : []
          }
          controlStyleWidth="130px"
          onChange={handleQuestionChange}
          isDisabled={!dropDownQuestionOptions || dropDownQuestionOptions.length === 0}
        />

        {selectedQuestion && (
          <>
            <Heading className="mt-2" type={'6'} fontSize={'0.9rem'}>
              IF
            </Heading>

            {/* Type 1, 2, 4, 7, 16 - Multiple choice, single choice, likert scale, ranking, NPS */}
            {[1, 2, 4, 7].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <DesignDropdown
                key={`cdl-choice-${selectedQuestion?.selectedChoiceID ?? item?.choice_ID ?? 'none'}`}
                options={selectedQuestion?.selectedQuestionAttached?.choiceName_array?.map(val => ({
                  value: getChoiceOptionId(val),
                  label: val.choiceName
                }))}
                value={resolveChoiceDropdownValue(selectedQuestion?.selectedChoiceID)}
                controlStyleWidth="100px"
                onChange={e => {
                  setSelectedQuestion(prev => ({ ...prev, selectedChoiceID: e.value }));
                  setHasEdited(true);
                }}
              />
            )}
            {[14].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <DesignDropdown
                key={`cdl-rank-${selectedQuestion?.selectedChoiceID ?? item?.choice_ID ?? 'none'}`}
                options={selectedQuestion?.selectedQuestionAttached?.choiceName_array?.map(val => ({
                  value: getChoiceOptionId(val),
                  label: val.choiceName
                }))}
                value={resolveChoiceDropdownValue(selectedQuestion?.selectedChoiceID)}
                controlStyleWidth="80px"
                onChange={e => {
                  setSelectedQuestion(prev => ({ ...prev, selectedChoiceID: e.value }));
                  setHasEdited(true);
                }}
              />
            )}
            {selectedQuestion?.selectedQuestionAttached?.questionType_ID == 16 && (
              <DesignDropdown
                key={`cdl-nps-${selectedQuestion?.npsScale_ID ?? 'none'}`}
                options={selectedQuestion?.selectedQuestionAttached?.likertScaleName_array?.map((val, index) => ({
                  // Use index when scale IDs are null so options are selectable.
                  value: val.questionLikertScale_ID ?? index,
                  label: `${index}`
                }))}
                value={resolveNpsDropdownValue()}
                controlStyleWidth="80px"
                onChange={e => {
                  setSelectedQuestion(prev => ({ ...prev, npsScale_ID: e.value }));
                  setHasEdited(true);
                }}
              />
            )}

            {selectedQuestion?.selectedQuestionAttached?.questionType_ID == 4 && (
              <DesignDropdown
                options={selectedQuestion?.selectedQuestionAttached?.likertScaleName_array?.map(val => ({
                  value: val.questionLikertScale_ID,
                  label: val.questionLikertScaleName
                }))}
                onChange={e => {
                  setSelectedQuestion(prev => ({ ...prev, LikertScaleID: e.value, likertSelectedLabel: e.label }));
                  setHasEdited(true);
                }}
                controlStyleWidth="80px"
                value={(() => {
                  // Always resolve label from scale list — likertSelectedLabel alone was blank on edit.
                  const scale = selectedQuestion?.selectedQuestionAttached?.likertScaleName_array?.find(
                    s => String(s.questionLikertScale_ID) === String(attachedLikertId)
                  );
                  return scale
                    ? [{ label: scale.questionLikertScaleName, value: scale.questionLikertScale_ID }]
                    : [];
                })()}
              />
            )}

            {/* {taskQuestion?.selectedQuestion?.questionType_ID == 14 && (
              <DesignDropdown
                options={taskQuestion?.selectedQuestion?.choiceName_array?.map(val => ({
                  value: val.choiceName_ID || val,
                  label: val.choiceName
                }))}
                onChange={e => {
                  setTaskQuestion(prev => ({ ...prev, selectedChoiceID: e.value }));
                  setHasEdited(true);
                }}
                controlStyleWidth="70px"
                value={answerChoice ? [{ label: answerChoice.label, value: answerChoice.value }] : []}
              />
            )} */}
            {/* Condition dropdown - "Contains" for text questions */}

            {/* Type 5, 6 - Text answer questions */}
            {[5, 6].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <InputText
                showCharacters={false}
                fontSize="14px"
                value={item?.ifContains || selectedQuestion?.selectedChoiceID}
                placeholder={'Enter text to match'}
                width="80px"
                style={{ color: 'var(--greyBlue)', fontWeight: '700', fontSize: '16px' }}
                onChange={e => {
                  setSelectedQuestion(prev => ({ ...prev, selectedChoiceID: e.target.value }));
                  setHasEdited(true);
                }}
              />
            )}
            {[5, 6].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <DesignDropdown
                options={[{ value: 'Contains', label: 'Contains' }]}
                value={[{ value: 'Contains', label: 'Contains' }]}
                controlStyleWidth="80px"
              />
            )}
            {[14].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <Heading className="mt-2" type={'6'} fontSize={'0.9rem'}>
                RANKED
              </Heading>
            )}

            {/* Type 3, 11, 14 - Value questions with comparibility */}
            {[3, 11, 14].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <>
                <DesignDropdown
                  options={
                    selectedQuestion?.selectedQuestionAttached?.MSTCompatibility_IDs?.map(val => ({
                      value: val.id,
                      label: val.Operations
                    })) || []
                  }
                  controlStyleWidth="90px"
                  onChange={e => {
                    setSelectedQuestion(prev => ({ ...prev, selectedComparibilityID: e.value }));
                    setHasEdited(true);
                  }}
                  value={(() => {
                    const id = hasEdited
                      ? selectedQuestion?.selectedComparibilityID
                      : selectedQuestion?.selectedComparibilityID ?? item?.comparibility_ID;
                    const op = selectedQuestion?.selectedQuestionAttached?.MSTCompatibility_IDs?.find(
                      v => String(v.id) === String(id)
                    );
                    return op ? [{ label: op.Operations, value: op.id }] : [];
                  })()}
                />
                <InputText
                  showCharacters={false}
                  fontSize="14px"
                  value={
                    hasEdited
                      ? selectedQuestion?.selectedChoiceValue ?? ''
                      : selectedQuestion?.selectedChoiceValue ?? item?.comparibility_value ?? ''
                  }
                  placeholder={'Enter value'}
                  width="60px"
                  onChange={e => {
                    setSelectedQuestion(prev => ({ ...prev, selectedChoiceValue: e.target.value }));
                    setHasEdited(true);
                  }}
                />
              </>
            )}

            {/* Condition dropdown - "Is Selected" for choice-based questions */}
            {[1, 2, 4, 7, 16, 3,11].includes(selectedQuestion?.selectedQuestionAttached?.questionType_ID) && (
              <DesignDropdown
                options={[{ value: 'Is selected', label: 'Is Selected' }]}
                value={[{ label: 'Is selected', value: 'Is selected' }]}
                controlStyleWidth="80px"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ConditionalDisplayLogic;
